import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { GenericStreamListForUserUse, GenericUserStreamWithProgress, StreamOrderItem } from '../../../../interfaces/responsedata.ts/generic-streams';
import { AppdataService } from '../../../../service/appdata.service';
import { CategoryService } from '../../../../service/category.service';
import { GenAiService } from '../../../../service/gen-ai.service';
import { GenericStreamService } from '../../../../service/generic-stream.service';
import { SnackService } from '../../../../service/snack.service';
import { UserdataService } from '../../../../service/userdata.service';
import { NameStreamComponent } from '../../generic-stream-dashboard/name-stream/name-stream.component';
import { CongratsComponent } from './congrats/congrats.component';

@Component({
    selector: 'app-generic-result-screen',
    standalone: false,
    templateUrl: './generic-result-screen.component.html',
    styleUrl: './generic-result-screen.component.scss'
})
export class GenericResultScreenComponent implements OnInit {
    genService = inject(GenericStreamService);
    ai = inject(GenAiService);
    sanitizer = inject(DomSanitizer);
    dialog = inject(MatDialog);
    user = inject(UserdataService);
    snack = inject(SnackService);
    destroyRef = inject(DestroyRef);
    router = inject(Router);
    dataService = inject(AppdataService);
    catService = inject(CategoryService);

    // Stream Data
    streamData: GenericStreamListForUserUse | undefined;
    userStream: GenericUserStreamWithProgress | undefined;

    isLoading: boolean = false;
    streamType: string = ""

    userId: string = this.user.getUserDataByKey('id')!;
    connectedStreams: any[] = []
    streamResultList: any[] = [];

    async ngOnInit(): Promise<void> {
        let urlPart = window.location.href.split('/').pop() || '';
        let decodedUrlPart = urlPart;
        let previousValue = '';


        while (decodedUrlPart !== previousValue) {
            previousValue = decodedUrlPart;
            decodedUrlPart = decodeURIComponent(decodedUrlPart);
        }

        let [streamId, queryString] = decodedUrlPart.split('?');
        let cid = null;

        if (queryString) {
            const params = new URLSearchParams(queryString);
            cid = params.get('cid');
        }

        if (cid && streamId) {
            let d = await this.genService.getOneGenericStream(cid);
            if (d === false) return;
            this.streamData = d;

            this.streamType = this.streamData.categoryId[this.streamData.categoryId.length - 1]
            let s = await this.genService.getOneGenericUserStream(streamId);
            if (s === false) return;
            this.userStream = s;
            // console.log("Stream", d);
            // console.log("UserStream", s);


            if (this.userStream.finished === false) {
                this.genService.updateUserGateProgress(this.userStream.configId)
                let dia = this.dialog.open(CongratsComponent, {
                    autoFocus: false,
                    panelClass: "small-dialog"
                });
                dia.afterClosed().subscribe(result => {
                    this.genService.updateGenericUserStreamFinished(this.userStream!.id)
                });
            }

            /**
             * Create next streams Preview
             */
            const streamList = await this.dataService.loadStructuredCategoriesAndStreams();

            if (streamList !== false) {
                this.dataService.streamCategories.subscribe(async (categories) => {
                    if (categories) {
                        let categoryTree = await this.catService.buildCategorieTree(categories) || [];
                        let packages = this.catService.flattenChildrenById(d.categoryId[0], categoryTree);

                        let finalList: StreamOrderItem[] = []

                        for (const pack of packages) {
                            const packStreams = streamList.filter((s: StreamOrderItem) => s.categoryId === pack.id)
                            if (packStreams.length > 0) {
                                finalList = [...finalList, ...packStreams]
                            }
                        }

                        const start = finalList.findIndex((pack) => pack.streamId === d.id);
                        finalList = finalList.slice(start + 1, start + 4);

                        for (const fs of finalList) {
                            let d = await this.genService.getOneGenericStream(fs.streamId);
                            const c = categories.find(c => c.id === fs.categoryId);
                            if (d !== false && c) {
                                this.connectedStreams.push({
                                    streamName: d.name,
                                    credits: d.credits,
                                    id: d.id,
                                    catName: c.name,
                                    stepCount: d.steps.length,
                                    description: c.description,
                                    stream: d
                                });
                            }
                        }

                    }
                });

            }



        }
    }

    protected async onStreamCardClick(
        streamId: string | undefined,
    ): Promise<void> {
        if (!streamId) {
            return;
        }

        // TODO: Get stream from next connected stream list -> api request
        // const stream = this.genstreams.find(s => s.id === streamId);
        const stream = this.connectedStreams.find(s => s.id === streamId).stream;

        // check if stream exists
        if (!stream) {
            this.snack.error(
                {
                    error: {
                        msg: {
                            headline: 'Error',
                            text: 'Selected stream could not be found.',
                        },
                    },
                });

            return;
        }

        // check if user has enough credits
        const userCredits = this.user.data.getValue()?.licenseData?.[0]?.credits;

        if (
            userCredits === undefined ||
            (
                userCredits !== -1 &&
                stream.credits > userCredits
            )
        ) {
            this.snack.error(
                {
                    error: {
                        msg: {
                            headline: 'Error',
                            text: 'Not enough credits to create this stream. Please add some credits or choose a different stream.',
                        },
                    },
                });

            return;
        }

        const dialog = this.dialog.open(NameStreamComponent, {
            panelClass: 'small-dialog',
            autoFocus: false,
            data: { name: stream.name }
        });

        dialog.afterClosed()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: async (name) => {
                    if (!name) {
                        return;
                    }

                    if (stream && typeof name === 'string') {
                        this.isLoading = true;

                        const newStreamId = await this.genService.createGenericUserStream(stream.id, name);

                        this.isLoading = false;

                        if (newStreamId === false) {
                            return;
                        };

                        this.router.navigate(['/generic-stream', newStreamId], { queryParams: { cid: stream.id } });
                    }
                }
            });
    }
}
